/**
 * @file
 * @fileOverview This file contains the terrain components for the Aframe scene.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

import { createNoise2D } from 'simplex-noise';


AFRAME.registerComponent('procedural-terrain-optimized', {
  schema: {
    chunkSize: { type: 'number', default: 10 },
    maxHeight: { type: 'number', default: 10 },
    seaLevel: { type: 'number', default: 0.3 },
    loadDistance: { type: 'number', default: 3 },
    cullingDistance: { type: 'number', default: 5 },
    lodLevels: { type: 'number', default: 4 },
    textureScale: { type: 'number', default: 1 },
    baseUrl: { type: 'string', default: '' },
    waterTexture: { type: 'string', default: '' },
    waterTextureNormal: { type: 'string', default: '' },
    waterTextureAO: { type: 'string', default: '' },
    waterTextureRoughness: { type: 'string', default: '' },
    waterTextureDisplacement: { type: 'string', default: '' },
    sandTexture: { type: 'string', default: '' },
    sandTextureNormal: { type: 'string', default: '' },
    sandTextureAO: { type: 'string', default: '' },
    sandTextureRoughness: { type: 'string', default: '' },
    sandTextureDisplacement: { type: 'string', default: '' },
    grassTexture: { type: 'string', default: '' },
    grassTextureNormal: { type: 'string', default: '' },
    grassTextureAO: { type: 'string', default: '' },
    grassTextureRoughness: { type: 'string', default: '' },
    grassTextureDisplacement: { type: 'string', default: '' },
    rockTexture: { type: 'string', default: '' },
    rockTextureNormal: { type: 'string', default: '' },
    rockTextureAO: { type: 'string', default: '' },
    rockTextureRoughness: { type: 'string', default: '' },
    rockTextureDisplacement: { type: 'string', default: '' },
    snowTexture: { type: 'string', default: '' },
    snowTextureNormal: { type: 'string', default: '' },
    snowTextureAO: { type: 'string', default: '' },
    snowTextureRoughness: { type: 'string', default: '' },
    snowTextureDisplacement: { type: 'string', default: '' },
    debug: { type: 'boolean', default: false }
  },

  init: function() {
    this.chunks = new Map();
    this.noise2D = createNoise2D();
    this.camera = this.el.sceneEl.camera;
    this.frustum = new THREE.Frustum();
    this.cameraViewProjectionMatrix = new THREE.Matrix4();
    this.loadTextures();
    this.createGeometryLODs();

    this.el.sceneEl.addEventListener('renderstart', this.checkChunks.bind(this));

    this.el.getTerrainHeight = this.getHeight.bind(this);

    if (this.data.debug) {
      console.log('Procedural Terrain initialized with settings:', this.data);
    }
  },

  loadTextures: function() {
    const textureLoader = new THREE.TextureLoader();
    const loadTexture = (url, mapType) => {
      if (!url) return null;
      const fullUrl = this.getFullUrl(url);
      return new Promise((resolve, reject) => {
        textureLoader.load(
          fullUrl,
          (texture) => {
            texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
            texture.repeat.set(this.data.textureScale, this.data.textureScale);
            if (this.data.debug) {
              console.log(`Texture loaded successfully: ${mapType} - ${fullUrl}`);
            }
            resolve(texture);
          },
          undefined,
          (error) => {
            console.error(`Error loading texture ${mapType}:`, fullUrl, error);
            reject(error);
          }
        );
      });
    };

    const biomes = ['water', 'sand', 'grass', 'rock', 'snow'];
    const mapTypes = ['', 'Normal', 'AO', 'Roughness', 'Displacement'];

    this.textures = {};

    const texturePromises = biomes.flatMap(biome => 
      mapTypes.map(mapType => {
        const textureKey = `${biome}${mapType}`;
        const url = this.data[`${biome}Texture${mapType}`];
        console.log('terrain texture url: ', url);
        return loadTexture(url, textureKey).then(texture => [textureKey, texture]);
      })
    );

    Promise.all(texturePromises)
      .then(loadedTextures => {
        loadedTextures.forEach(([key, texture]) => {
          if (texture) {
            this.textures[key] = texture;
          }
        });
        if (this.data.debug) {
          console.log('All textures loaded successfully', this.textures);
        }
        this.generateInitialChunks();
      })
      .catch(error => {
        console.error('Error loading textures:', error);
      });
  },

  getFullUrl: function(url) {
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) {
      return url;
    } else {
      return this.data.baseUrl + url;
    }
  },

  createGeometryLODs: function() {
    this.geometryLODs = [];
    for (let i = 0; i < this.data.lodLevels; i++) {
      const detailLevel = Math.max(8, Math.floor(32 / Math.pow(2, i)));
      const geometry = new THREE.BoxGeometry(
        this.data.chunkSize,
        this.data.maxHeight,
        this.data.chunkSize,
        detailLevel,
        1,
        detailLevel
      );
      this.geometryLODs.push(geometry);
    }
    if (this.data.debug) {
      console.log('Created LOD geometries:', this.geometryLODs);
    }
  },

  createMaterial: function(biome) {
    const material = new THREE.MeshStandardMaterial({
      map: this.textures[biome],
      normalMap: this.textures[`${biome}Normal`],
      aoMap: this.textures[`${biome}AO`],
      roughnessMap: this.textures[`${biome}Roughness`],
      displacementMap: this.textures[`${biome}Displacement`],
      displacementScale: 0.1,
      displacementBias: 0,
      side: THREE.DoubleSide
    });

    if (this.textures[`${biome}AO`]) {
      material.aoMapIntensity = 1.0;
    }

    return material;
  },

  generateChunk: function(chunkX, chunkZ) {
    const chunkKey = `${chunkX},${chunkZ}`;
    if (this.chunks.has(chunkKey)) return;

    const geometry = this.geometryLODs[0].clone();
    const positions = geometry.attributes.position.array;
    const uvs = geometry.attributes.uv.array;

    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i] + chunkX * this.data.chunkSize;
      const z = positions[i + 2] + chunkZ * this.data.chunkSize;

      const { height, biome } = this.getTerrainFeatures(x, z);
      const normalizedHeight = height / this.data.maxHeight;
      positions[i + 1] = (positions[i + 1] + 1) * 0.5 * height;

      const uvIndex = (i / 3) * 2;
      uvs[uvIndex] = (x / this.data.chunkSize) * this.data.textureScale;
      uvs[uvIndex + 1] = (z / this.data.chunkSize) * this.data.textureScale;
    }

    geometry.computeVertexNormals();
    
    if (geometry.attributes.uv) {
      geometry.setAttribute('uv2', geometry.attributes.uv.clone());
    }

    const material = this.createMaterial(this.getBiome(positions[1], chunkX, chunkZ));
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(chunkX * this.data.chunkSize, 0, chunkZ * this.data.chunkSize);
    mesh.frustumCulled = true;
    mesh.chunkKey = chunkKey;

    this.el.object3D.add(mesh);
    this.chunks.set(chunkKey, { mesh, lastUsed: performance.now() });

    if (this.data.debug) {
      console.log('Generated chunk:', chunkKey, 'at position:', mesh.position);
    }
  },

  getTerrainFeatures: function(x, z) {
    const height = this.getFractalNoise(x, z);
    const biome = this.getBiome(height, x, z);
    return { height, biome };
  },

  getFractalNoise: function(x, z) {
    let amplitude = 1;
    let frequency = 0.005;
    let noise = 0;
    let maxValue = 0;
    const octaves = 6;
    const lacunarity = 2;
    const persistence = 0.5;

    for (let i = 0; i < octaves; i++) {
      noise += this.noise2D(x * frequency, z * frequency) * amplitude;
      maxValue += amplitude;
      amplitude *= persistence;
      frequency *= lacunarity;
    }

    noise = noise / maxValue;
    noise = Math.pow(Math.abs(noise) * 1.3, 2.5);

    return noise * this.data.maxHeight;
  },

  getBiome: function(height, x, z) {
    if (height < this.data.seaLevel * this.data.maxHeight) return 'water';
    if (height < (this.data.seaLevel + 0.01) * this.data.maxHeight) return 'sand';

    const temperature = (this.noise2D(x * 0.001, z * 0.001) + 1) / 2;
    const moisture = (this.noise2D(x * 0.002, z * 0.002) + 1) / 2;

    if (temperature < 0.2) return 'snow';
    if (temperature < 0.4) return moisture > 0.5 ? 'grass' : 'rock';
    if (temperature < 0.7) return moisture > 0.5 ? 'grass' : 'rock';
    return moisture > 0.5 ? 'grass' : 'sand';
  },

  checkChunks: function() {
    const cameraPosition = this.camera.position;
    const currentChunk = {
      x: Math.floor(cameraPosition.x / this.data.chunkSize),
      z: Math.floor(cameraPosition.z / this.data.chunkSize)
    };

    this.frustum.setFromProjectionMatrix(
      this.cameraViewProjectionMatrix.multiplyMatrices(
        this.camera.projectionMatrix,
        this.camera.matrixWorldInverse
      )
    );

    for (let x = -this.data.loadDistance; x <= this.data.loadDistance; x++) {
      for (let z = -this.data.loadDistance; z <= this.data.loadDistance; z++) {
        const chunkX = currentChunk.x + x;
        const chunkZ = currentChunk.z + z;
        this.generateChunk(chunkX, chunkZ);
      }
    }

    let visibleChunks = 0;
    for (const [key, chunk] of this.chunks) {
      const [x, z] = key.split(',').map(Number);
      const distance = Math.max(Math.abs(x - currentChunk.x), Math.abs(z - currentChunk.z));
      
      if (distance > this.data.cullingDistance) {
        this.el.object3D.remove(chunk.mesh);
        this.chunks.delete(key);
      } else {
        chunk.lastUsed = performance.now();
        this.updateChunkLOD(chunk.mesh, distance);
        chunk.mesh.visible = this.frustum.intersectsObject(chunk.mesh);
        if (chunk.mesh.visible) visibleChunks++;
      }
    }

    if (this.data.debug) {
      console.log('Visible chunks:', visibleChunks, 'Total chunks:', this.chunks.size);
    }

    this.cleanupUnusedChunks();
  },

  updateChunkLOD: function(mesh, distance) {
    const lodLevel = Math.min(Math.floor(distance / 2), this.data.lodLevels - 1);
    if (mesh.geometry !== this.geometryLODs[lodLevel]) {
      const oldPositions = mesh.geometry.attributes.position.array;
      const newGeometry = this.geometryLODs[lodLevel].clone();
      const newPositions = newGeometry.attributes.position.array;

      for (let i = 0; i < newPositions.length; i += 3) {
        const x = newPositions[i];
        const z = newPositions[i + 2];
        const oldIndex = this.findNearestPointIndex(oldPositions, x, z);
        newPositions[i + 1] = oldPositions[oldIndex + 1];
      }

      newGeometry.attributes.position.needsUpdate = true;
      newGeometry.computeVertexNormals();
      mesh.geometry.dispose();
      mesh.geometry = newGeometry;
    }
  },

  findNearestPointIndex: function(positions, x, z) {
    let nearestIndex = 0;
    let minDistance = Infinity;

    for (let i = 0; i < positions.length; i += 3) {
      const dx = positions[i] - x;
      const dz = positions[i + 2] - z;
      const distance = dx * dx + dz * dz;

      if (distance < minDistance) {
        minDistance = distance;
        nearestIndex = i;
      }
    }

    return nearestIndex;
  },

  cleanupUnusedChunks: function() {
    const now = performance.now();
    for (const [key, chunk] of this.chunks) {
      if (now - chunk.lastUsed > 30000) {
        this.el.object3D.remove(chunk.mesh);
        chunk.mesh.geometry.dispose();
        chunk.mesh.material.dispose();
        this.chunks.delete(key);
      }
    }
  },

generateInitialChunks: function() {
    const centerChunk = { x: 0, z: 0 };
    for (let x = -this.data.loadDistance; x <= this.data.loadDistance; x++) {
      for (let z = -this.data.loadDistance; z <= this.data.loadDistance; z++) {
        this.generateChunk(centerChunk.x + x, centerChunk.z + z);
      }
    }
    if (this.data.debug) {
      console.log('Initial chunks generated:', this.chunks.size);
    }
  },

  getHeight: function(x, z) {
    return this.getTerrainFeatures(x, z).height;
  },

  remove: function() {
    for (const chunk of this.chunks.values()) {
      this.el.object3D.remove(chunk.mesh);
      chunk.mesh.geometry.dispose();
      chunk.mesh.material.dispose();
    }
    this.chunks.clear();
    this.geometryLODs.forEach(g => g.dispose());
    if (this.data.debug) {
      console.log('Terrain component removed and resources cleaned up');
    }
  }
});


AFRAME.registerComponent('procedural-terrain', {
    schema: {
      chunkSize: { type: 'number', default: 10 },
      maxHeight: { type: 'number', default: 10 },
      seaLevel: { type: 'number', default: 0.3 },
      loadDistance: { type: 'number', default: 3 },
      cullingDistance: { type: 'number', default: 5 },
      lodLevels: { type: 'number', default: 4 },
      textureScale: { type: 'number', default: 1 },
      waterTexture: { type: 'asset' },
      sandTexture: { type: 'asset' },
      grassTexture: { type: 'asset' },
      rockTexture: { type: 'asset' },
      snowTexture: { type: 'asset' },
      debug: { type: 'boolean', default: false }
    },
  
    init: function() {
      this.chunks = new Map();
      this.noise2D = createNoise2D();
      this.camera = this.el.sceneEl.camera;
      this.frustum = new THREE.Frustum();
      this.cameraViewProjectionMatrix = new THREE.Matrix4();
      this.loadTextures();
      this.createGeometryLODs();
  
      this.el.sceneEl.addEventListener('renderstart', this.checkChunks.bind(this));
  
      this.el.getTerrainHeight = this.getHeight.bind(this);
  
      if (this.data.debug) {
        console.log('Procedural Terrain initialized with settings:', this.data);
      }
    },
  
    loadTextures: function() {
        const textureLoader = new THREE.TextureLoader();
        const loadTexture = (assetItem) => {
          if (!assetItem) return null;
          let src;
          if (typeof assetItem === 'string') {
            src = assetItem;
          } else if (assetItem instanceof HTMLImageElement) {
            src = assetItem.src;
          } else if (assetItem.getAttribute) {
            src = assetItem.getAttribute('src');
          }
          if (!src) {
            console.warn('Invalid texture source:', assetItem);
            return null;
          }
    
          return new Promise((resolve, reject) => {
            textureLoader.load(
              src,
              (texture) => {
                texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(this.data.textureScale, this.data.textureScale);
                texture.minFilter = THREE.LinearMipMapLinearFilter;
                texture.magFilter = THREE.LinearFilter;
                texture.anisotropy = 16;
                if (this.data.debug) {
                  console.log(`Texture loaded successfully: ${src}`);
                }
                resolve(texture);
              },
              undefined,
              (error) => {
                console.error(`Error loading texture ${src}:`, error);
                reject(error);
              }
            );
          });
        };
    
        const texturePromises = {
          water: loadTexture(this.data.waterTexture),
          sand: loadTexture(this.data.sandTexture),
          grass: loadTexture(this.data.grassTexture),
          rock: loadTexture(this.data.rockTexture),
          snow: loadTexture(this.data.snowTexture)
        };
    
        Promise.all(Object.values(texturePromises))
          .then((textures) => {
            this.textures = {
              water: textures[0],
              sand: textures[1],
              grass: textures[2],
              rock: textures[3],
              snow: textures[4]
            };
            if (this.data.debug) {
              console.log('All textures loaded successfully', this.textures);
            }
            this.generateInitialChunks();
          })
          .catch((error) => {
            console.error('Error loading textures:', error);
          });
      },
      
    createGeometryLODs: function() {
      this.geometryLODs = [];
      for (let i = 0; i < this.data.lodLevels; i++) {
        const detailLevel = Math.max(8, Math.floor(32 / Math.pow(2, i)));
        const geometry = new THREE.PlaneGeometry(
          this.data.chunkSize,
          this.data.chunkSize,
          detailLevel,
          detailLevel
        );
        this.geometryLODs.push(geometry);
      }
      if (this.data.debug) {
        console.log('Created LOD geometries:', this.geometryLODs);
      }
    },
  
    generateChunk: function(chunkX, chunkZ) {
        const chunkKey = `${chunkX},${chunkZ}`;
        if (this.chunks.has(chunkKey)) return;
    
        const geometry = this.geometryLODs[0].clone();
        const positions = geometry.attributes.position.array;
        const uvs = geometry.attributes.uv.array;
    
        for (let i = 0; i < positions.length; i += 3) {
          const x = positions[i] + chunkX * this.data.chunkSize;
          const z = positions[i + 2] + chunkZ * this.data.chunkSize;
    
          const { height, biome } = this.getTerrainFeatures(x, z);
          const normalizedHeight = height / this.data.maxHeight;
          positions[i + 1] = (positions[i + 1] + 1) * 0.5 * height;
    
          const uvIndex = (i / 3) * 2;
          uvs[uvIndex] = (x / this.data.chunkSize) * this.data.textureScale;
          uvs[uvIndex + 1] = (z / this.data.chunkSize) * this.data.textureScale;
        }
    
        geometry.computeVertexNormals();
        
        const material = this.createMaterial(this.getBiome(positions[1], chunkX, chunkZ));
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.set(chunkX * this.data.chunkSize, 0, chunkZ * this.data.chunkSize);
        mesh.frustumCulled = true;
        mesh.chunkKey = chunkKey;
    
        this.el.object3D.add(mesh);
        this.chunks.set(chunkKey, { mesh, lastUsed: performance.now() });
    
        if (this.data.debug) {
          console.log('Generated chunk:', chunkKey, 'at position:', mesh.position);
        }
      },
      
      createMaterial: function(biome) {
        const texture = this.textures[biome];
        if (!texture) {
          console.warn(`No texture found for biome: ${biome}`);
          return new THREE.MeshStandardMaterial({ color: 0x808080, side: THREE.DoubleSide });
        }
        return new THREE.MeshStandardMaterial({
          map: texture,
          side: THREE.DoubleSide
        });
      },
    
  
    getTerrainFeatures: function(x, z) {
      const height = this.getFractalNoise(x, z);
      const biome = this.getBiome(height, x, z);
      return { height, biome };
    },
  
    getFractalNoise: function(x, z) {
      let amplitude = 1;
      let frequency = 0.005;
      let noise = 0;
      let maxValue = 0;
      const octaves = 6;
      const lacunarity = 2;
      const persistence = 0.5;
  
      for (let i = 0; i < octaves; i++) {
        noise += this.noise2D(x * frequency, z * frequency) * amplitude;
        maxValue += amplitude;
        amplitude *= persistence;
        frequency *= lacunarity;
      }
  
      noise = noise / maxValue;
      noise = Math.pow(Math.abs(noise) * 1.3, 2.5);
  
      return noise * this.data.maxHeight;
    },
  
    getBiome: function(height, x, z) {
      if (height < this.data.seaLevel * this.data.maxHeight) return 'water';
      if (height < (this.data.seaLevel + 0.01) * this.data.maxHeight) return 'sand';
  
      const temperature = (this.noise2D(x * 0.001, z * 0.001) + 1) / 2;
      const moisture = (this.noise2D(x * 0.002, z * 0.002) + 1) / 2;
  
      if (temperature < 0.2) return 'snow';
      if (temperature < 0.4) return moisture > 0.5 ? 'grass' : 'rock';
      if (temperature < 0.7) return moisture > 0.5 ? 'grass' : 'rock';
      return moisture > 0.5 ? 'grass' : 'sand';
    },
  
    checkChunks: function() {
      const cameraPosition = this.camera.position;
      const currentChunk = {
        x: Math.floor(cameraPosition.x / this.data.chunkSize),
        z: Math.floor(cameraPosition.z / this.data.chunkSize)
      };
  
      this.frustum.setFromProjectionMatrix(
        this.cameraViewProjectionMatrix.multiplyMatrices(
          this.camera.projectionMatrix,
          this.camera.matrixWorldInverse
        )
      );
  
      for (let x = -this.data.loadDistance; x <= this.data.loadDistance; x++) {
        for (let z = -this.data.loadDistance; z <= this.data.loadDistance; z++) {
          const chunkX = currentChunk.x + x;
          const chunkZ = currentChunk.z + z;
          this.generateChunk(chunkX, chunkZ);
        }
      }
  
      let visibleChunks = 0;
      for (const [key, chunk] of this.chunks) {
        const [x, z] = key.split(',').map(Number);
        const distance = Math.max(Math.abs(x - currentChunk.x), Math.abs(z - currentChunk.z));
        
        if (distance > this.data.cullingDistance) {
          this.el.object3D.remove(chunk.mesh);
          this.chunks.delete(key);
        } else {
          chunk.lastUsed = performance.now();
          this.updateChunkLOD(chunk.mesh, distance);
          chunk.mesh.visible = this.frustum.intersectsObject(chunk.mesh);
          if (chunk.mesh.visible) visibleChunks++;
        }
      }
  
      if (this.data.debug) {
        console.log('Visible chunks:', visibleChunks, 'Total chunks:', this.chunks.size);
      }
  
      this.cleanupUnusedChunks();
    },
  
    updateChunkLOD: function(mesh, distance) {
      const lodLevel = Math.min(Math.floor(distance / 2), this.data.lodLevels - 1);
      if (mesh.geometry !== this.geometryLODs[lodLevel]) {
        const oldPositions = mesh.geometry.attributes.position.array;
        const newGeometry = this.geometryLODs[lodLevel].clone();
        const newPositions = newGeometry.attributes.position.array;
  
        for (let i = 0; i < newPositions.length; i += 3) {
          const x = newPositions[i];
          const z = newPositions[i + 2];
          const oldIndex = this.findNearestPointIndex(oldPositions, x, z);
          newPositions[i + 1] = oldPositions[oldIndex + 1];
        }
  
        newGeometry.attributes.position.needsUpdate = true;
        newGeometry.computeVertexNormals();
        mesh.geometry.dispose();
        mesh.geometry = newGeometry;
      }
    },
  
    findNearestPointIndex: function(positions, x, z) {
      let nearestIndex = 0;
      let minDistance = Infinity;
  
      for (let i = 0; i < positions.length; i += 3) {
        const dx = positions[i] - x;
        const dz = positions[i + 2] - z;
        const distance = dx * dx + dz * dz;
  
        if (distance < minDistance) {
          minDistance = distance;
          nearestIndex = i;
        }
      }
  
      return nearestIndex;
    },
  
    cleanupUnusedChunks: function() {
      const now = performance.now();
      for (const [key, chunk] of this.chunks) {
        if (now - chunk.lastUsed > 30000) { // 30 seconds
          this.el.object3D.remove(chunk.mesh);
          chunk.mesh.geometry.dispose();
          chunk.mesh.material.forEach(m => m.dispose());
          this.chunks.delete(key);
        }
      }
    },
  
    generateInitialChunks: function() {
      const centerChunk = { x: 0, z: 0 };
      for (let x = -this.data.loadDistance; x <= this.data.loadDistance; x++) {
        for (let z = -this.data.loadDistance; z <= this.data.loadDistance; z++) {
          this.generateChunk(centerChunk.x + x, centerChunk.z + z);
        }
      }
      if (this.data.debug) {
        console.log('Initial chunks generated:', this.chunks.size);
      }
    },
  
    getHeight: function(x, z) {
      return this.getTerrainFeatures(x, z).height;
    },
  
    remove: function() {
      for (const chunk of this.chunks.values()) {
        this.el.object3D.remove(chunk.mesh);
        chunk.mesh.geometry.dispose();
        chunk.mesh.material.forEach(m => m.dispose());
      }
      this.chunks.clear();
      this.geometryLODs.forEach(g => g.dispose());
      if (this.data.debug) {
        console.log('Terrain component removed and resources cleaned up');
      }
    }
  });




AFRAME.registerComponent('procedural-terrain-optimized-old', {
  schema: {
    chunkSize: { type: 'number', default: 10 },
    maxHeight: { type: 'number', default: 10 },
    seaLevel: { type: 'number', default: 0.3 },
    loadDistance: { type: 'number', default: 3 },
    cullingDistance: { type: 'number', default: 5 },
    lodLevels: { type: 'number', default: 4 },
    textureScale: { type: 'number', default: 1 },
    waterTexture: { type: 'asset' },
    sandTexture: { type: 'asset' },
    grassTexture: { type: 'asset' },
    rockTexture: { type: 'asset' },
    snowTexture: { type: 'asset' },
    debug: { type: 'boolean', default: false }
  },

  init: function() {
    this.chunks = new Map();
    this.noise2D = createNoise2D();
    this.camera = this.el.sceneEl.camera;
    this.frustum = new THREE.Frustum();
    this.cameraViewProjectionMatrix = new THREE.Matrix4();
    this.loadTextures();
    this.createGeometryLODs();

    this.el.sceneEl.addEventListener('renderstart', this.checkChunks.bind(this));

    this.el.getTerrainHeight = this.getHeight.bind(this);

    if (this.data.debug) {
      console.log('Procedural Terrain initialized with settings:', this.data);
    }
  },

  loadTextures: function() {
    const textureLoader = new THREE.TextureLoader();
    const loadTexture = (assetItem) => {
      if (!assetItem) return null;
      let src;
      if (typeof assetItem === 'string') {
        src = assetItem;
      } else if (assetItem instanceof HTMLImageElement) {
        src = assetItem.src;
      } else if (assetItem.getAttribute) {
        src = assetItem.getAttribute('src');
      }
      if (!src) {
        console.warn('Invalid texture source:', assetItem);
        return null;
      }

      return new Promise((resolve, reject) => {
        textureLoader.load(
          src,
          (texture) => {
            texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
            texture.repeat.set(this.data.textureScale, this.data.textureScale);
            texture.minFilter = THREE.LinearMipMapLinearFilter;
            texture.magFilter = THREE.LinearFilter;
            texture.anisotropy = 16;
            if (this.data.debug) {
              console.log(`Texture loaded successfully: ${src}`);
            }
            resolve(texture);
          },
          undefined,
          (error) => {
            console.error(`Error loading texture ${src}:`, error);
            reject(error);
          }
        );
      });
    };

    const texturePromises = {
      water: loadTexture(this.data.waterTexture),
      sand: loadTexture(this.data.sandTexture),
      grass: loadTexture(this.data.grassTexture),
      rock: loadTexture(this.data.rockTexture),
      snow: loadTexture(this.data.snowTexture)
    };

    Promise.all(Object.values(texturePromises))
      .then(() => {
        if (this.data.debug) {
          console.log('All textures loaded successfully');
        }
        this.generateInitialChunks();
      })
      .catch((error) => {
        console.error('Error loading textures:', error);
      });

    this.textures = Object.fromEntries(
      Object.entries(texturePromises).map(([key, promise]) => [key, { map: null }])
    );

    Object.entries(texturePromises).forEach(([key, promise]) => {
      promise.then(texture => {
        this.textures[key].map = texture;
      });
    });
  },

  createGeometryLODs: function() {
    this.geometryLODs = [];
    for (let i = 0; i < this.data.lodLevels; i++) {
      const detailLevel = Math.max(8, Math.floor(32 / Math.pow(2, i)));
      const geometry = new THREE.BoxGeometry(
        this.data.chunkSize,
        this.data.maxHeight,
        this.data.chunkSize,
        detailLevel,
        1,
        detailLevel
      );
      this.geometryLODs.push(geometry);
    }
    if (this.data.debug) {
      console.log('Created LOD geometries:', this.geometryLODs);
    }
  },

  generateChunk: function(chunkX, chunkZ) {
    const chunkKey = `${chunkX},${chunkZ}`;
    if (this.chunks.has(chunkKey)) return;

    const geometry = this.geometryLODs[0].clone();
    const positions = geometry.attributes.position.array;
    const uvs = geometry.attributes.uv.array;

    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i] + chunkX * this.data.chunkSize;
      const z = positions[i + 2] + chunkZ * this.data.chunkSize;

      const { height, biome } = this.getTerrainFeatures(x, z);
      const normalizedHeight = height / this.data.maxHeight;
      positions[i + 1] = (positions[i + 1] + 1) * 0.5 * height;

      const uvIndex = (i / 3) * 2;
      uvs[uvIndex] = (x / this.data.chunkSize) * this.data.textureScale;
      uvs[uvIndex + 1] = (z / this.data.chunkSize) * this.data.textureScale;
    }

    geometry.computeVertexNormals();
    
    const materials = this.createMaterials();
    const mesh = new THREE.Mesh(geometry, materials);
    mesh.position.set(chunkX * this.data.chunkSize, 0, chunkZ * this.data.chunkSize);
    mesh.frustumCulled = true;
    mesh.chunkKey = chunkKey;

    this.el.object3D.add(mesh);
    this.chunks.set(chunkKey, { mesh, lastUsed: performance.now() });

    if (this.data.debug) {
      console.log('Generated chunk:', chunkKey, 'at position:', mesh.position);
    }
  },

  createMaterials: function() {
    const materials = [];
    for (const [biome, textures] of Object.entries(this.textures)) {
      const material = new THREE.MeshStandardMaterial({
        map: textures.map,
        side: THREE.DoubleSide
      });
      materials.push(material);
    }
    return materials;
  },

  getTerrainFeatures: function(x, z) {
    const height = this.getFractalNoise(x, z);
    const biome = this.getBiome(height, x, z);
    return { height, biome };
  },

  getFractalNoise: function(x, z) {
    let amplitude = 1;
    let frequency = 0.005;
    let noise = 0;
    let maxValue = 0;
    const octaves = 6;
    const lacunarity = 2;
    const persistence = 0.5;

    for (let i = 0; i < octaves; i++) {
      noise += this.noise2D(x * frequency, z * frequency) * amplitude;
      maxValue += amplitude;
      amplitude *= persistence;
      frequency *= lacunarity;
    }

    noise = noise / maxValue;
    noise = Math.pow(Math.abs(noise) * 1.3, 2.5);

    return noise * this.data.maxHeight;
  },

  getBiome: function(height, x, z) {
    if (height < this.data.seaLevel * this.data.maxHeight) return 'water';
    if (height < (this.data.seaLevel + 0.01) * this.data.maxHeight) return 'sand';

    const temperature = (this.noise2D(x * 0.001, z * 0.001) + 1) / 2;
    const moisture = (this.noise2D(x * 0.002, z * 0.002) + 1) / 2;

    if (temperature < 0.2) return 'snow';
    if (temperature < 0.4) return moisture > 0.5 ? 'grass' : 'rock';
    if (temperature < 0.7) return moisture > 0.5 ? 'grass' : 'rock';
    return moisture > 0.5 ? 'grass' : 'sand';
  },

  checkChunks: function() {
    const cameraPosition = this.camera.position;
    const currentChunk = {
      x: Math.floor(cameraPosition.x / this.data.chunkSize),
      z: Math.floor(cameraPosition.z / this.data.chunkSize)
    };

    this.frustum.setFromProjectionMatrix(
      this.cameraViewProjectionMatrix.multiplyMatrices(
        this.camera.projectionMatrix,
        this.camera.matrixWorldInverse
      )
    );

    for (let x = -this.data.loadDistance; x <= this.data.loadDistance; x++) {
      for (let z = -this.data.loadDistance; z <= this.data.loadDistance; z++) {
        const chunkX = currentChunk.x + x;
        const chunkZ = currentChunk.z + z;
        this.generateChunk(chunkX, chunkZ);
      }
    }

    let visibleChunks = 0;
    for (const [key, chunk] of this.chunks) {
      const [x, z] = key.split(',').map(Number);
      const distance = Math.max(Math.abs(x - currentChunk.x), Math.abs(z - currentChunk.z));
      
      if (distance > this.data.cullingDistance) {
        this.el.object3D.remove(chunk.mesh);
        this.chunks.delete(key);
      } else {
        chunk.lastUsed = performance.now();
        this.updateChunkLOD(chunk.mesh, distance);
        chunk.mesh.visible = this.frustum.intersectsObject(chunk.mesh);
        if (chunk.mesh.visible) visibleChunks++;
      }
    }

    if (this.data.debug) {
      console.log('Visible chunks:', visibleChunks, 'Total chunks:', this.chunks.size);
    }

    this.cleanupUnusedChunks();
  },

  updateChunkLOD: function(mesh, distance) {
    const lodLevel = Math.min(Math.floor(distance / 2), this.data.lodLevels - 1);
    if (mesh.geometry !== this.geometryLODs[lodLevel]) {
      const oldPositions = mesh.geometry.attributes.position.array;
      const newGeometry = this.geometryLODs[lodLevel].clone();
      const newPositions = newGeometry.attributes.position.array;

      for (let i = 0; i < newPositions.length; i += 3) {
        const x = newPositions[i];
        const z = newPositions[i + 2];
        const oldIndex = this.findNearestPointIndex(oldPositions, x, z);
        newPositions[i + 1] = oldPositions[oldIndex + 1];
      }

      newGeometry.attributes.position.needsUpdate = true;
      newGeometry.computeVertexNormals();
      mesh.geometry.dispose();
      mesh.geometry = newGeometry;
    }
  },

  findNearestPointIndex: function(positions, x, z) {
    let nearestIndex = 0;
    let minDistance = Infinity;

    for (let i = 0; i < positions.length; i += 3) {
      const dx = positions[i] - x;
      const dz = positions[i + 2] - z;
      const distance = dx * dx + dz * dz;

      if (distance < minDistance) {
        minDistance = distance;
        nearestIndex = i;
      }
    }

    return nearestIndex;
  },

  cleanupUnusedChunks: function() {
    const now = performance.now();
    for (const [key, chunk] of this.chunks) {
      if (now - chunk.lastUsed > 30000) {
        this.el.object3D.remove(chunk.mesh);
        chunk.mesh.geometry.dispose();
        chunk.mesh.material.forEach(m => m.dispose());
        this.chunks.delete(key);
      }
    }
  },

  generateInitialChunks: function() {
    const centerChunk = { x: 0, z: 0 };
    for (let x = -this.data.loadDistance; x <= this.data.loadDistance; x++) {
      for (let z = -this.data.loadDistance; z <= this.data.loadDistance; z++) {
        this.generateChunk(centerChunk.x + x, centerChunk.z + z);
      }
    }
    if (this.data.debug) {
      console.log('Initial chunks generated:', this.chunks.size);
    }
  },

  getHeight: function(x, z) {
    return this.getTerrainFeatures(x, z).height;
  },

  remove: function() {
    for (const chunk of this.chunks.values()) {
      this.el.object3D.remove(chunk.mesh);
      chunk.mesh.geometry.dispose();
      chunk.mesh.material.forEach(m => m.dispose());
    }
    this.chunks.clear();
    this.geometryLODs.forEach(g => g.dispose());
    if (this.data.debug) {
      console.log('Terrain component removed and resources cleaned up');
    }
  }
});


export const terrain = {
    proceduralterrain: AFRAME.components['procedural-terrain'],
    proceduralterrainOptimized: AFRAME.components['procedural-terrain-optimized'],


  };